import React, { memo } from 'react';
import { Select, Tag } from 'antd';
import { AiFillFolderOpen, AiOutlineWarning } from "react-icons/ai";
import { BsFillPencilFill } from "react-icons/bs";
import { getRowStyle, getChangedScoreClass, formatScore } from './tableHelpers';

const TableRow = memo(({
  user,
  index,
  calibratedScore,
  justificationIdNotFilled,
  usersRequiringJustification,
  filterColumn,
  previousRating,
  projectPhase,
  previousCalibration,
  latestScore,
  hasJustification,
  watch,
  SCORE_OPTIONS,
  RATING_OPTIONS,
  handleScoreSelectChange,
  handleScoreInputChange,
  handleScoreBlur,
  handleRatingChange,
  handleShowJustificationModal,
  setDetailActualScore,
  setSpmoComment,
  hanldeShowAdditionalInfoModal,
  isSavingScore,
  currentEditingUserId
}) => {
  // Check if this specific user requires justification
  const requiresJustification = usersRequiringJustification.has(user.ID);
  
  return (
    <>
      <style>
        {`
          /* Hide spin buttons for number inputs */
          input[type="number"]::-webkit-outer-spin-button,
          input[type="number"]::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          
          /* Firefox */
          input[type="number"] {
            -moz-appearance: textfield;
          }
        `}
      </style>
      <tr 
        key={user.ID} 
        style={getRowStyle(user, justificationIdNotFilled, usersRequiringJustification)}
      >
      <td>{index + 1}</td>

      {filterColumn.includes('Employee') && (
        <td>
          <p className="ms-0 mb-0">
            <b>{user.Name} </b>
            <span style={{ fontSize: "12px" }}>({user.Nik})</span>
          </p>
          <p className="ms-0 mb-0" style={{ fontSize: "12px" }}>
            {user.OrganizationUnit}
          </p>
        </td>
      )}

      {filterColumn.includes('Grade') && (
        <td className="text-center">{user.Grade}</td>
      )}

      {filterColumn.includes('Supervisor') && (
        <td>{user.SupervisorNames}</td>
      )}

      {filterColumn.includes('Division') && (
        <td className="text-center">{user.Division || '-'}</td>
      )}

      {filterColumn.includes('Prev. Rating') && (
        <td className="text-center">
          {user.ActualScores[0]?.[previousRating] || "-"}
        </td>
      )}

      {filterColumn.includes('Actual') && (
        <>
          <td
            className="text-center"
            data-tooltip-id="tooltip-actual-score"
            onMouseEnter={() => setDetailActualScore(user.ActualScores[0])}
          >
            {formatScore(user.ActualScores[0]?.ActualScore)}
          </td>
          <td className="text-center">
            {user.ActualScores[0]?.ActualRating}
          </td>
        </>
      )}

      {filterColumn.includes('Calibration (Previous)') && projectPhase && projectPhase.Phase.Order >= 2 && (
        <>
          {previousCalibration?.CalibrationScore && previousCalibration?.CalibrationRating ? (
            <>
              <td className="text-center">
                {user.ScoringMethod === 'Score' ? formatScore(previousCalibration.CalibrationScore) : "-"}
              </td>
              <td className="text-center">
                {previousCalibration.CalibrationRating}
              </td>
            </>
          ) : (
            <>
              <td className="text-center">-</td>
              <td className="text-center">-</td>
            </>
          )}
        </>
      )}

      {filterColumn.includes('Calibration') && (
        <>
          <td className={getChangedScoreClass(user, calibratedScore)}>
            {user.ScoringMethod === "Score" ? (
              <input
                type="number"
                style={{ 
                  width: '80px', 
                  textAlign: 'center',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '14px',
                  opacity: (isSavingScore && currentEditingUserId !== user.ID) ? 0.5 : 1,
                  cursor: (isSavingScore && currentEditingUserId !== user.ID) ? 'not-allowed' : 'text'
                }}
                disabled={watch || user.ScoringMethod !== "Score" || (isSavingScore && currentEditingUserId !== user.ID)}
                min="0"
                max="5"
                step="0.01"
                placeholder="0.00"
                value={calibratedScore[user.ID]?.CalibrationScore || ''}
                onChange={(e) => handleScoreInputChange(e, user)}
                onFocus={(e) => e.target.select()} // Auto-select all text when focused
                onBlur={(e) => handleScoreBlur(e, user)}
                onKeyDown={(e) => {
                  // Handle Enter key
                  if (e.key === 'Enter') {
                    e.target.blur(); // This will trigger onBlur
                    return;
                  }
                  
                  // Allow: backspace, delete, tab, escape, enter, decimal point, and numbers
                  if ([
                    'Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 
                    'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                    'Home', 'End'
                  ].includes(e.key)) {
                    return; // Allow these keys
                  }
                  
                  // Allow decimal point only if there isn't one already
                  if (e.key === '.') {
                    if (e.target.value.includes('.')) {
                      e.preventDefault(); // Prevent multiple decimal points
                    }
                    return;
                  }
                  
                  // Allow only numbers
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            ) : "-"}
          </td>
          <td className={getChangedScoreClass(user, calibratedScore)}>
            <Select
              style={{ width: 70 }}
              disabled={watch || user.ScoringMethod !== "Rating" || (isSavingScore && currentEditingUserId !== user.ID)}
              fontSize={10}
              showSearch="true"
              options={RATING_OPTIONS.slice(1)}
              placeholder="Enter Rating"
              onChange={(e) => handleRatingChange(e, user)}
              value={calibratedScore[user.ID]?.CalibrationRating}
            />
          </td>
        </>
      )}

      {filterColumn.includes('Status') && (
        <td className="text-center">
          <Tag
            className="mb-2 mt-2"
            color={
              latestScore.Status === 'Waiting' ? 'yellow' :
              latestScore.Status === 'Complete' ? 'green' : 'blue'
            }
            data-tooltip-id="tooltip-status"
          >
            {latestScore.Status}
          </Tag>
          {latestScore.SpmoStatus === 'Rejected' && (
            <Tag
              className="mb-2 mt-2"
              color={
                latestScore.SpmoStatus === 'Accepted' ? 'green' :
                latestScore.SpmoStatus === 'Rejected' ? 'red' :
                latestScore.SpmoStatus === 'Waiting' ? 'yellow' : ''
              }
              data-tooltip-id="tooltip-spmo-status"
              onMouseEnter={() => setSpmoComment(latestScore.SpmoComment)}
            >
              {latestScore.SpmoStatus}
            </Tag>
          )}
        </td>
      )}

      {filterColumn.includes('Justification') && (
        <td className="text-end" style={{ verticalAlign: 'middle' }}>
          {/* Show warning icon only if user has AdditionalInfo */}
          {user.AdditionalInfo && (
            <button
              className="me-2"
              onClick={() => hanldeShowAdditionalInfoModal(user)}
              title="View additional information"
              style={{ 
                background: 'none', 
                border: 'none', 
                padding: '0', 
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                verticalAlign: 'middle'
              }}
            >
              <AiOutlineWarning fontSize={16} color='#FF9F43' />
            </button>
          )}
          {hasJustification && (
            <button
              className="transparent-button me-2"
              title="View justification"
              onClick={() => handleShowJustificationModal(user, true)}
              disabled={isSavingScore}
              style={{ verticalAlign: 'middle', opacity: isSavingScore ? 0.5 : 1, cursor: isSavingScore ? 'not-allowed' : 'pointer' }}
            >
              <AiFillFolderOpen fontSize={16} />
            </button>
          )}
          {!watch && (
            <button
              className={`btn ${requiresJustification ? 'btn-danger' : 'btn-outline-primary'} btn-sm`}
              onClick={() => handleShowJustificationModal(user, false)}
              disabled={isSavingScore}
              style={{
                animation: requiresJustification ? 'pulse 1.5s infinite' : 'none',
                height: 'min-content',
                borderRadius: '5px',
                padding: '3px 5px !important',
                minWidth: '24px',
                fontSize: '12px',
                verticalAlign: 'middle',
                opacity: isSavingScore ? 0.5 : 1,
                cursor: isSavingScore ? 'not-allowed' : 'pointer'
              }}
              title={requiresJustification ? 'Justification required!' : 'Add justification'}
            >
              <BsFillPencilFill style={{ fontSize: '12px' }} />
            </button>
          )}
        </td>
      )}
    </tr>
    </>
  );
});

TableRow.displayName = 'TableRow';

export default TableRow;